/* ==========================================================================
   Future Aesthetics — header
   <sticky-header>   hides on scroll down, reveals on scroll up
   <fa-mega-menu>    opens on hover for pointers, click/keyboard for everyone
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     Sticky header
     ------------------------------------------------------------------------ */

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = document.querySelector('.section-header');
      if (!this.header) return;

      this.alwaysSticky = this.dataset.stickyType === 'always';
      this.currentScrollTop = 0;
      this.headerBounds = {};

      this.setHeaderHeight();
      this.resizeObserver = new ResizeObserver(() => this.setHeaderHeight());
      this.resizeObserver.observe(this.header);

      if (this.alwaysSticky) this.header.classList.add('shopify-section-header-sticky');

      this.onScrollHandler = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });

      const observer = new IntersectionObserver((entries, obs) => {
        this.headerBounds = entries[0].intersectionRect;
        obs.disconnect();
      });
      observer.observe(this.header);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScrollHandler);
      this.resizeObserver?.disconnect();
    }

    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      // A search or menu panel is open — leave the header exactly where it is.
      if (this.querySelector('details[open]')) {
        this.currentScrollTop = scrollTop;
        return;
      }

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        requestAnimationFrame(() => this.hide());
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        requestAnimationFrame(() => this.reveal());
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(() => this.reset());
      }

      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (this.alwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    }

    reveal() {
      if (this.alwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
    }

    reset() {
      if (this.alwaysSticky) return;
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }
  }

  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

  /* ------------------------------------------------------------------------
     Mega menu
     ------------------------------------------------------------------------ */

  const OPEN_DELAY = 60;
  const CLOSE_DELAY = 180;

  class FaMegaMenu extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      if (!this.details || !this.summary) return;

      this.summary.setAttribute('aria-expanded', 'false');

      // Pointer devices open on hover; touch and keyboard use the native
      // <details> toggle, which keeps the menu usable with JS disabled.
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.addEventListener('mouseenter', () => this.scheduleOpen());
        this.addEventListener('mouseleave', () => this.scheduleClose());
      }

      this.addEventListener('focusin', () => this.open());
      this.addEventListener('focusout', () => {
        setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        });
      });

      this.details.addEventListener('toggle', () => {
        this.summary.setAttribute('aria-expanded', String(this.details.open));
        if (this.details.open) this.closeSiblings();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.details.open) {
          this.close();
          this.summary.focus();
        }
      });
    }

    closeSiblings() {
      document.querySelectorAll('fa-mega-menu').forEach((menu) => {
        if (menu !== this) menu.close();
      });
    }

    scheduleOpen() {
      clearTimeout(this.closeTimer);
      this.openTimer = setTimeout(() => this.open(), OPEN_DELAY);
    }

    scheduleClose() {
      clearTimeout(this.openTimer);
      this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
    }

    open() {
      clearTimeout(this.closeTimer);
      if (this.details) this.details.open = true;
    }

    close() {
      clearTimeout(this.openTimer);
      if (this.details) this.details.open = false;
    }
  }

  if (!customElements.get('fa-mega-menu')) customElements.define('fa-mega-menu', FaMegaMenu);

  // Clicking anywhere outside the navigation closes whatever is open.
  document.addEventListener('click', (event) => {
    if (event.target.closest('fa-mega-menu')) return;
    document.querySelectorAll('fa-mega-menu').forEach((menu) => menu.close());
  });
})();
